import { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, AlertCircle, Loader2, ArrowLeft, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TILES = [
  { value: "selling", label: "Considering selling" },
  { value: "exploring", label: "Understanding my property's potential" },
  { value: "land", label: "Land opportunity" },
  { value: "opportunity", label: "Development opportunity" },
  { value: "received", label: "Received a letter from us" },
  { value: "other", label: "Just have a question" },
];

const TEAM = ["No preference", "Jeremy Alfred", "Taj Alfred", "Robert Swearengen III"];
const RELATIONSHIPS = ["Owner", "Co-owner", "Authorized representative", "Other"];
const PROPERTY_TYPES = ["House", "Small multifamily", "Vacant land", "Commercial", "Multiple adjoining properties", "Other"];
const TIMELINES = ["As soon as practical", "Within 30 days", "1–3 months", "Flexible"];
const OCCUPANCY = ["Owner-occupied", "Tenant-occupied", "Vacant", "Other"];
const CONDITION = ["Move-in ready", "Minor repairs", "Significant repairs", "Unsure", "Land only"];
const LAND_INTERESTS = ["Selling options", "Potential land use", "Neighborhood changes", "Something else"];
const ROLES = ["Agent or broker", "Property owner", "Builder or developer", "Investor", "Other"];
const LIGHT = ["received", "exploring", "other"];
const PARTNER = ["opportunity", "investor", "professional"];

const STEP2_TITLES = {
  selling: "About the property",
  land: "Your land",
  opportunity: "The opportunity",
  investor: "The opportunity",
  professional: "The opportunity",
};

const inputCls =
  "w-full rounded-sm border border-[#D9D6CE] bg-white px-4 py-3 text-base text-[#111513] placeholder:text-[#9AA49C] focus:border-[#A77A50] focus:ring-2 focus:ring-[#A77A50]/25 focus:outline-none transition";

const Field = ({ label, optional, hint, error, children, htmlFor }) => (
  <div>
    <label htmlFor={htmlFor} className="block text-sm font-semibold text-[#111513] mb-1.5">
      {label} {optional && <span className="font-normal text-[#718477]">(optional)</span>}
    </label>
    {children}
    {hint && <p className="mt-1.5 text-xs text-[#718477]">{hint}</p>}
    {error && (
      <p className="mt-1.5 text-sm text-red-800" role="alert">
        {error}
      </p>
    )}
  </div>
);

const RadioCards = ({ name, options, value, onChange, testIdPrefix }) => (
  <div className="grid gap-2.5" role="radiogroup" aria-label={name}>
    {options.map((o) => {
      const val = typeof o === "string" ? o : o.value;
      const lab = typeof o === "string" ? o : o.label;
      const checked = value === val;
      return (
        <label
          key={val}
          data-testid={`${testIdPrefix}-${val.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
          className={`flex items-center gap-3 rounded-sm border px-4 py-3 cursor-pointer text-base transition-colors ${
            checked
              ? "border-[#A77A50] bg-[#A77A50]/5 text-[#111513] font-medium"
              : "border-[#D9D6CE] bg-white text-[#3E4742] hover:border-[#A77A50]/60"
          }`}
        >
          <input
            type="radio"
            name={name}
            value={val}
            checked={checked}
            onChange={() => onChange(val)}
            className="accent-[#18352C] w-4 h-4 shrink-0"
          />
          {lab}
        </label>
      );
    })}
  </div>
);

export const InquiryForm = () => {
  const [form, setForm] = useState({
    firstName: "", lastName: "", email: "", phone: "", method: "Email", category: "", teamMember: "No preference",
  });
  const [answers, setAnswers] = useState({});
  const [honeypot, setHoneypot] = useState("");
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");
  const [step, setStep] = useState(1);
  const submissionId = useRef(crypto.randomUUID());

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setA = (k, v) => setAnswers((a) => ({ ...a, [k]: v }));
  const onA = (k) => (e) => setA(k, e.target.value);
  const toggleList = (k, item) =>
    setAnswers((a) => {
      const cur = Array.isArray(a[k]) ? a[k] : [];
      return { ...a, [k]: cur.includes(item) ? cur.filter((x) => x !== item) : [...cur, item] };
    });

  const validateBranch = (c) => {
    const e = {};
    if (LIGHT.includes(c) && !(answers.message || "").trim()) e.message = "Please tell us what you'd like to ask.";
    if (c === "land" && !(answers.message || "").trim()) e.message = "Please tell us what you'd like to understand or ask.";
    if (c === "selling") {
      if (!(answers.property_address || "").trim()) e.property_address = "Please enter the property address.";
      if (!answers.relationship) e.relationship = "Please choose your relationship to the property.";
      if (!answers.property_type) e.property_type = "Please choose the property type.";
      if (!answers.timeline) e.timeline = "Please choose your preferred timeline.";
    }
    if (PARTNER.includes(c)) {
      if (!(answers.description || "").trim()) e.description = "Please describe the opportunity.";
      if (!(answers.relationship_to_opportunity || "").trim())
        e.relationship_to_opportunity = "Please tell us your relationship to the opportunity.";
    }
    return e;
  };

  const validateContact = () => {
    const e = {};
    if (!form.firstName.trim()) e.firstName = "Please enter your first name.";
    if (!form.lastName.trim()) e.lastName = "Please enter your last name.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) e.email = "Please enter a valid email address.";
    if ((form.method === "Phone" || form.method === "Text") && !form.phone.trim())
      e.phone = "A phone number is required when phone or text is your preferred method.";
    return e;
  };

  const goNext = () => {
    let e = {};
    if (step === 1 && !form.category) e = { category: "Please choose the option that fits best." };
    if (step === 2) e = validateBranch(form.category);
    setErrors(e);
    if (Object.keys(e).length === 0) setStep((s) => Math.min(3, s + 1));
  };

  const pickCategory = (v) => {
    setForm((f) => ({ ...f, category: v }));
    setAnswers({});
    setErrors({});
    setTimeout(() => setStep(2), 280);
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (status === "submitting") return;
    const e = { ...validateBranch(form.category), ...validateContact() };
    if (!form.category) e.category = "Please choose the option that fits best.";
    setErrors(e);
    if (Object.keys(e).length > 0) return;
    setStatus("submitting");
    try {
      const res = await fetch(`${API}/inquiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          submission_id: submissionId.current,
          first_name: form.firstName,
          last_name: form.lastName,
          email: form.email,
          phone: form.phone || null,
          preferred_contact_method: form.method,
          category: form.category,
          team_member: form.teamMember === "No preference" ? null : form.teamMember,
          answers,
          website: honeypot,
          source_page: window.location.href,
        }),
      });
      if (!res.ok) throw new Error("Request failed");
      setStatus("success");
    } catch {
      setStatus("error");
    }
  };

  const reset = () => {
    submissionId.current = crypto.randomUUID();
    setForm({ firstName: "", lastName: "", email: "", phone: "", method: "Email", category: "", teamMember: "No preference" });
    setAnswers({});
    setErrors({});
    setStatus("idle");
    setStep(1);
  };

  if (status === "success") {
    return (
      <div data-testid="inquiry-success-message" className="rounded-sm border border-[#A77A50]/40 bg-white p-8 sm:p-12 text-center">
        <CheckCircle2 className="w-10 h-10 text-[#A77A50] mx-auto" aria-hidden="true" />
        <h3 className="mt-5 font-display text-3xl text-[#111513]">Thank you for reaching out.</h3>
        <p className="mt-4 text-base text-[#3E4742] leading-relaxed max-w-md mx-auto">
          Your inquiry has been received by Choose Me Properties. Our team will review what you shared and respond
          using your preferred contact method.
        </p>
        <button
          type="button"
          data-testid="inquiry-submit-another-button"
          onClick={reset}
          className="mt-7 text-[13px] font-semibold tracking-[0.12em] uppercase text-[#A77A50] border-b border-[#A77A50] pb-1 hover:text-[#111513] transition-colors"
        >
          Submit another inquiry
        </button>
      </div>
    );
  }

  const c = form.category;
  const stepTitles = { 1: "What brings you here?", 2: STEP2_TITLES[c] || "Your question", 3: "How do we reach you?" };

  return (
    <form
      id="inquiry-form"
      data-testid="inquiry-form"
      onSubmit={submit}
      noValidate
      className="rounded-sm bg-white border border-[#D9D6CE] p-6 sm:p-10 lg:p-12 scroll-mt-28"
    >
      <div className="absolute -left-[9999px] top-auto" aria-hidden="true">
        <label htmlFor="website">Website</label>
        <input id="website" name="website" type="text" tabIndex={-1} autoComplete="off" value={honeypot} onChange={(e) => setHoneypot(e.target.value)} />
      </div>

      <div className="flex items-baseline justify-between">
        <p className="text-[11px] font-semibold tracking-[0.28em] uppercase text-[#718477]">
          {String(step).padStart(2, "0")} / 03
        </p>
        <p className="text-[11px] font-semibold tracking-[0.28em] uppercase text-[#A77A50]">Choose Me Properties</p>
      </div>
      <div className="mt-3 h-px bg-[#D9D6CE]" aria-hidden="true">
        <motion.div
          className="h-px bg-[#A77A50]"
          animate={{ width: `${(step / 3) * 100}%` }}
          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
        />
      </div>
      <h3 className="mt-7 font-display text-2xl sm:text-3xl text-[#111513]">{stepTitles[step]}</h3>

      <AnimatePresence mode="wait">
        <motion.div
          key={step}
          initial={{ opacity: 0, x: 24 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -24 }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          className="mt-7"
        >
          {step === 1 && (
            <div>
              <div className="grid sm:grid-cols-2 gap-3" role="radiogroup" aria-label="What brings you here?">
                {TILES.map((t) => {
                  const on = form.category === t.value;
                  return (
                    <button
                      key={t.value}
                      type="button"
                      role="radio"
                      aria-checked={on}
                      data-testid={`contact-form-category-${t.value}`}
                      onClick={() => pickCategory(t.value)}
                      className={`group flex items-center justify-between gap-3 rounded-sm border px-5 py-5 text-left transition-colors ${
                        on ? "border-[#A77A50] bg-[#A77A50]/5" : "border-[#D9D6CE] bg-white hover:border-[#A77A50]/60"
                      }`}
                    >
                      <span className={`font-display text-lg sm:text-xl leading-snug ${on ? "text-[#111513]" : "text-[#3E4742]"}`}>
                        {t.label}
                      </span>
                      <span
                        className={`w-2.5 h-2.5 rotate-45 shrink-0 transition-colors ${on ? "bg-[#A77A50]" : "bg-[#D9D6CE] group-hover:bg-[#A77A50]/50"}`}
                        aria-hidden="true"
                      />
                    </button>
                  );
                })}
              </div>
              {errors.category && (
                <p className="mt-2 text-sm text-red-800" role="alert">{errors.category}</p>
              )}
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6">
              {LIGHT.includes(c) && (
                <>
                  <Field label="Property address, if applicable" optional htmlFor="light-address">
                    <input id="light-address" data-testid="contact-form-address-input" className={inputCls} value={answers.property_address || ""} onChange={onA("property_address")} />
                  </Field>
                  <Field label="What would you like to ask us?" htmlFor="light-message" error={errors.message}>
                    <textarea id="light-message" rows={4} data-testid="contact-form-message-input" className={inputCls} value={answers.message || ""} onChange={onA("message")} />
                  </Field>
                </>
              )}

              {c === "land" && (
                <>
                  <Field label="Property city or neighborhood" optional htmlFor="land-location">
                    <input id="land-location" data-testid="contact-form-location-input" className={inputCls} value={answers.location || ""} onChange={onA("location")} />
                  </Field>
                  <Field label="What would you like to understand?" optional htmlFor="land-interests">
                    <div className="flex flex-wrap gap-2.5">
                      {LAND_INTERESTS.map((i) => {
                        const on = (answers.interests || []).includes(i);
                        return (
                          <button
                            key={i}
                            type="button"
                            data-testid={`contact-form-interest-${i.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                            aria-pressed={on}
                            onClick={() => toggleList("interests", i)}
                            className={`rounded-full border px-4 py-2 text-sm font-medium transition-colors ${
                              on ? "border-[#A77A50] bg-[#A77A50]/10 text-[#111513]" : "border-[#D9D6CE] text-[#3E4742] hover:border-[#A77A50]/60"
                            }`}
                          >
                            {i}
                          </button>
                        );
                      })}
                    </div>
                  </Field>
                  <Field label="What would you like to ask us?" htmlFor="land-message" error={errors.message}>
                    <textarea id="land-message" rows={4} data-testid="contact-form-land-message-input" className={inputCls} value={answers.message || ""} onChange={onA("message")} />
                  </Field>
                </>
              )}

              {c === "selling" && (
                <>
                  <Field label="Property address" htmlFor="sell-address" error={errors.property_address}>
                    <input id="sell-address" data-testid="contact-form-address-input" className={inputCls} value={answers.property_address || ""} onChange={onA("property_address")} autoComplete="street-address" />
                  </Field>
                  <Field label="Your relationship to the property" htmlFor="sell-relationship" error={errors.relationship}>
                    <RadioCards name="relationship" options={RELATIONSHIPS} value={answers.relationship || ""} onChange={(v) => setA("relationship", v)} testIdPrefix="contact-form-relationship" />
                  </Field>
                  <Field label="Property type" htmlFor="sell-type" error={errors.property_type}>
                    <RadioCards name="property_type" options={PROPERTY_TYPES} value={answers.property_type || ""} onChange={(v) => setA("property_type", v)} testIdPrefix="contact-form-property-type" />
                  </Field>
                  <Field label="Preferred timeline" htmlFor="sell-timeline" error={errors.timeline}>
                    <RadioCards name="timeline" options={TIMELINES} value={answers.timeline || ""} onChange={(v) => setA("timeline", v)} testIdPrefix="contact-form-timeline" />
                  </Field>
                  <p className="text-[11px] font-semibold tracking-[0.24em] uppercase text-[#718477] pt-2">
                    Helpful details — all optional
                  </p>
                  <div className="grid sm:grid-cols-2 gap-5">
                    <Field label="Occupancy" optional htmlFor="sell-occupancy">
                      <select id="sell-occupancy" data-testid="contact-form-occupancy-select" className={inputCls} value={answers.occupancy || ""} onChange={onA("occupancy")}>
                        <option value="">Select…</option>
                        {OCCUPANCY.map((o) => <option key={o} value={o}>{o}</option>)}
                      </select>
                    </Field>
                    <Field label="General condition" optional htmlFor="sell-condition">
                      <select id="sell-condition" data-testid="contact-form-condition-select" className={inputCls} value={answers.condition || ""} onChange={onA("condition")}>
                        <option value="">Select…</option>
                        {CONDITION.map((o) => <option key={o} value={o}>{o}</option>)}
                      </select>
                    </Field>
                  </div>
                  <Field label="Do you have an asking price or price range in mind?" optional htmlFor="sell-price" hint="It's completely fine to write “Not sure.”">
                    <input id="sell-price" data-testid="contact-form-price-input" className={inputCls} value={answers.price_range || ""} onChange={onA("price_range")} />
                  </Field>
                  <Field label="What is prompting you to consider selling?" optional htmlFor="sell-prompt" hint="Share only what you're comfortable sharing.">
                    <textarea id="sell-prompt" rows={3} data-testid="contact-form-prompt-input" className={inputCls} value={answers.prompt || ""} onChange={onA("prompt")} />
                  </Field>
                  <div className="grid sm:grid-cols-2 gap-5">
                    <Field label="Are other owners or decision-makers involved?" optional htmlFor="sell-decision-makers">
                      <select id="sell-decision-makers" data-testid="contact-form-decision-makers-select" className={inputCls} value={answers.other_decision_makers || ""} onChange={onA("other_decision_makers")}>
                        <option value="">Select…</option>
                        {["Yes", "No", "Unsure"].map((o) => <option key={o} value={o}>{o}</option>)}
                      </select>
                    </Field>
                    <Field label="Is the property currently listed with an agent?" optional htmlFor="sell-listed">
                      <select id="sell-listed" data-testid="contact-form-listed-select" className={inputCls} value={answers.listed_with_agent || ""} onChange={onA("listed_with_agent")}>
                        <option value="">Select…</option>
                        {["Yes", "No"].map((o) => <option key={o} value={o}>{o}</option>)}
                      </select>
                    </Field>
                  </div>
                  <Field label="Are there known property issues or deadlines we should understand?" optional htmlFor="sell-issues" hint="For example: repairs, access, title questions, tenant considerations, or a preferred closing date.">
                    <textarea id="sell-issues" rows={3} data-testid="contact-form-issues-input" className={inputCls} value={answers.known_issues || ""} onChange={onA("known_issues")} />
                  </Field>
                  <Field label="What would a good outcome look like for you?" optional htmlFor="sell-outcome">
                    <textarea id="sell-outcome" rows={3} data-testid="contact-form-outcome-input" className={inputCls} value={answers.good_outcome || ""} onChange={onA("good_outcome")} />
                  </Field>
                </>
              )}

              {PARTNER.includes(c) && (
                <>
                  <Field label="Your role" htmlFor="partner-role">
                    <select id="partner-role" data-testid="contact-form-role-select" className={inputCls} value={answers.role || ""} onChange={onA("role")}>
                      <option value="">Select…</option>
                      {ROLES.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </Field>
                  <div className="grid sm:grid-cols-2 gap-5">
                    <Field label="Company" optional htmlFor="partner-company">
                      <input id="partner-company" data-testid="contact-form-company-input" className={inputCls} value={answers.company || ""} onChange={onA("company")} autoComplete="organization" />
                    </Field>
                    <Field label="Opportunity location" optional htmlFor="partner-location">
                      <input id="partner-location" data-testid="contact-form-opportunity-location-input" className={inputCls} value={answers.location || ""} onChange={onA("location")} />
                    </Field>
                  </div>
                  <Field label="Describe the opportunity and how you'd like to work together." htmlFor="partner-description" error={errors.description}>
                    <textarea id="partner-description" rows={4} data-testid="contact-form-description-input" className={inputCls} value={answers.description || ""} onChange={onA("description")} />
                  </Field>
                  <Field label="What is your relationship to the property or opportunity?" htmlFor="partner-relationship" error={errors.relationship_to_opportunity}>
                    <input id="partner-relationship" data-testid="contact-form-opportunity-relationship-input" className={inputCls} value={answers.relationship_to_opportunity || ""} onChange={onA("relationship_to_opportunity")} />
                  </Field>
                </>
              )}
            </div>
          )}

          {step === 3 && (
            <div className="space-y-6">
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="First Name" htmlFor="first-name" error={errors.firstName}>
                  <input id="first-name" data-testid="contact-form-first-name-input" className={inputCls} value={form.firstName} onChange={set("firstName")} autoComplete="given-name" />
                </Field>
                <Field label="Last Name" htmlFor="last-name" error={errors.lastName}>
                  <input id="last-name" data-testid="contact-form-last-name-input" className={inputCls} value={form.lastName} onChange={set("lastName")} autoComplete="family-name" />
                </Field>
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Email Address" htmlFor="email" error={errors.email}>
                  <input id="email" type="email" data-testid="contact-form-email-input" className={inputCls} value={form.email} onChange={set("email")} autoComplete="email" />
                </Field>
                <Field label="Phone Number" optional={form.method === "Email"} htmlFor="phone" error={errors.phone}>
                  <input id="phone" type="tel" data-testid="contact-form-phone-input" className={inputCls} value={form.phone} onChange={set("phone")} autoComplete="tel" />
                </Field>
              </div>
              <Field label="Preferred Contact Method" htmlFor="method">
                <div className="flex flex-wrap gap-2.5" role="radiogroup" aria-label="Preferred contact method">
                  {["Email", "Phone", "Text"].map((m) => (
                    <label
                      key={m}
                      data-testid={`contact-form-method-${m.toLowerCase()}`}
                      className={`flex items-center gap-2 rounded-full border px-5 py-2.5 cursor-pointer text-sm font-medium transition-colors ${
                        form.method === m ? "border-[#111513] bg-[#111513] text-[#F4F1EA]" : "border-[#D9D6CE] text-[#3E4742] hover:border-[#111513]/50"
                      }`}
                    >
                      <input type="radio" name="method" value={m} checked={form.method === m} onChange={() => setForm((f) => ({ ...f, method: m }))} className="sr-only" />
                      {m}
                    </label>
                  ))}
                </div>
              </Field>
              <Field label="Who would you like to speak with?" optional htmlFor="team-member">
                <select id="team-member" data-testid="contact-form-team-member-select" className={inputCls} value={form.teamMember} onChange={set("teamMember")}>
                  {TEAM.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </Field>
            </div>
          )}
        </motion.div>
      </AnimatePresence>

      {status === "error" && (
        <div data-testid="inquiry-error-message" role="alert" className="mt-6 flex gap-3 rounded-sm border border-red-200 bg-red-50 p-4">
          <AlertCircle className="w-5 h-5 text-red-800 shrink-0 mt-0.5" aria-hidden="true" />
          <p className="text-sm text-red-900 leading-relaxed">
            We couldn&rsquo;t submit your inquiry. Please try again, email{" "}
            <a href="mailto:contact@choosemeproperties.com" className="font-semibold underline">contact@choosemeproperties.com</a>, or call{" "}
            <a href="tel:+12064831705" className="font-semibold underline">(206) 483-1705</a>. Your answers above are still here.
          </p>
        </div>
      )}

      <div className="mt-8 flex items-center gap-3">
        {step > 1 && (
          <button
            type="button"
            data-testid="contact-form-back-button"
            onClick={() => setStep((s) => Math.max(1, s - 1))}
            className="inline-flex items-center gap-2 rounded-sm border border-[#D9D6CE] text-[#3E4742] text-[13px] font-semibold tracking-[0.1em] uppercase px-6 py-4 hover:border-[#111513] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" aria-hidden="true" />
            Back
          </button>
        )}
        {step < 3 && (
          <button
            type="button"
            data-testid="contact-form-continue-button"
            onClick={goNext}
            className="flex-1 inline-flex items-center justify-center gap-2 rounded-sm bg-[#111513] text-[#F4F1EA] text-[13px] font-semibold tracking-[0.1em] uppercase px-8 py-4 hover:bg-[#18352C] transition-colors"
          >
            Continue
            <ArrowRight className="w-4 h-4" aria-hidden="true" />
          </button>
        )}
        {step === 3 && (
          <button
            type="submit"
            data-testid="contact-form-submit-button"
            disabled={status === "submitting"}
            className="flex-1 inline-flex items-center justify-center gap-2 rounded-sm bg-[#111513] text-[#F4F1EA] text-[13px] font-semibold tracking-[0.1em] uppercase px-8 py-4 hover:bg-[#18352C] disabled:opacity-60 transition-colors"
          >
            {status === "submitting" && <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />}
            {status === "submitting" ? "Sending…" : "Start the Conversation"}
          </button>
        )}
      </div>
      {step === 3 && (
        <p className="mt-5 text-xs text-[#718477] leading-relaxed text-center">
          By submitting, you&rsquo;re asking Choose Me Properties to respond to this inquiry. There&rsquo;s no
          obligation to sell. Please do not include Social Security numbers, banking details, or sensitive documents.
        </p>
      )}
    </form>
  );
};
